import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { RecordContextProvider, useGetOne } from 'react-admin';
import { Button, DeleteButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { ContextMenu, ContextMenuEntityUserActionsButton } from 'shared/mui/ContextMenu';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Link } from 'shared/mui/Link/Link';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { DateTimeField, FunctionField, TextField } from 'shared/react-admin/Fields';
import { MerchantCredentials } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';

type Props = {
  open: boolean;
  onEdit: (record: MerchantCredentials) => void;
  onDelete: (record: MerchantCredentials) => void;
  handleOpenCredentialsDialog: (record: MerchantCredentials) => void;
  onClose: () => void;
  id: string | undefined;
};

export const MerchantsShowCredentialsShowDialog: React.FC<Props> = ({
  open,
  onClose,
  onEdit,
  onDelete,
  id,
  handleOpenCredentialsDialog,
}) => {
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();

  const { data } = useGetOne<MerchantCredentials>(
    'merchant_credentials',
    {
      id: id || '',
    },
    {
      enabled: Boolean(open && id),
    },
  );

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{ '.MuiPaper-root': { width: '640px', maxWidth: '640px' } }}
    >
      <RecordContextProvider value={data}>
        <CardHeader
          appendActions={[
            <ContextMenu key="context-menu">
              {hasResourceAccess(Resources.UserActions) && (
                <ContextMenuEntityUserActionsButton entityName="merchant_credentials" />
              )}
            </ContextMenu>,
            <CloseIconButton key="close-button" onClose={onClose} />,
          ]}
          title="Просмотр реквизитов доступа"
          titleSize="smallBold"
        />
        <CardContent>
          <CardTable tableBodyProps={{ sx: { '& td': { width: '205px' } } }}>
            <TableRowWrapper label="ID">
              <TextField source="originId" />
            </TableRowWrapper>
            <TableRowWrapper label="Название">
              <TextField source="name" />
            </TableRowWrapper>
            <TableRowWrapper label="Провайдер">
              <FunctionField
                label="Провайдер"
                render={(record: MerchantCredentials) =>
                  record?.provider ? record.provider.name : EMPTY_FIELD
                }
              />
            </TableRowWrapper>
            <TableRowWrapper label="Комментарий">
              <TextField
                emptyText={EMPTY_FIELD}
                source="comment"
                sx={{
                  overflow: 'hidden',
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              />
            </TableRowWrapper>
            <TableRowWrapper label="Реквизиты доступа {json}">
              <Link
                onClick={() => {
                  data && handleOpenCredentialsDialog(data);
                  onClose();
                }}
                sx={{ cursor: 'pointer' }}
              >
                Показать
              </Link>
            </TableRowWrapper>
            <TableRowWrapper label="Дата создания">
              <DateTimeField inline source="createdAt" />
            </TableRowWrapper>
            <TableRowWrapper label="Дата редактир.">
              <DateTimeField inline source="updatedAt" />
            </TableRowWrapper>
          </CardTable>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            hasResourcePermissions('merchant_credentials', [
              Permission.Full,
              Permission.Delete,
            ]) && (
              <DeleteButton
                onClick={() => {
                  onClose();
                  data && onDelete(data);
                }}
                variant="outlined"
              />
            ),
            hasResourcePermissions('merchant_credentials', [Permission.Full, Permission.Update]) &&
              !data?.locked && (
                <Button
                  label="Редактировать"
                  onClick={() => {
                    onClose();
                    data && onEdit(data);
                  }}
                  variant="contained"
                />
              ),
          ]}
        />
      </RecordContextProvider>
    </Dialog>
  );
};
